/**
 * CobrandTabs Component
 *
 * A molecule component that provides tabbed navigation for cobrand cards.
 * Supports filtering of child components based on cardType (personal, business, none).
 *
 * Key Features:
 * - Tab-based navigation with active state management
 * - CardType filtering for showing/hiding content based on tab selection
 * - Edit mode support for AEM authoring
 * - Responsive design with configurable alignment and layout
 * - Performance optimized with memoization
 *
 * @module molecules/CobrandTabs
 */

import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from 'react';
import { clsx } from 'clsx';
import { Button, Heading, headingType, size, RichText } from '@marriott/mi-ui-library';
import { StyledTab } from './CobrandTabs.styles';
import { TabProps, CardType } from './CobrandTabs.types';
import { COBRAND_CARD_TYPE } from '../../modules/constants/constants';

/**
 * CobrandTabs Component
 *
 * Renders a tabbed interface for organizing and filtering cobrand card content.
 * Components with cardType="none" are hidden from all tabs.
 */
export const CobrandTabs = memo<TabProps>(
  ({
    componentId,
    trackingProperties,
    tabDetails,
    headerTag,
    header,
    headerFontSize,
    subHeader,
    subHeaderTag,
    subFontSize,
    descriptionText,
    descFontSize,
    contentBlockAlignment,
    layoutContainer,
    children,
    isEditMode = false,
    styleclass,
    customClass,
    componentPath,
  }: TabProps) => {
    // Refs for DOM elements
    const containerRef = useRef<HTMLDivElement>(null);

    // Filter and convert children to array
    const childrenArray = useMemo(() => React.Children.toArray(children).filter(Boolean), [children]);

    // State management
    const [activeTab, setActiveTab] = useState(0);

    // Derived state - memoized for performance
    const borderEnabled = useMemo(() => layoutContainer === 'with_border', [layoutContainer]);

    /**
     * Determines active state for each tab content
     * In edit mode: all tabs are active (visible)
     * In view mode: only the selected tab is active
     */
    const activeStates = useMemo(() => {
      if (isEditMode) {
        return new Array(childrenArray.length).fill(true);
      }
      return new Array(childrenArray.length).fill(false).map((_, index) => index === activeTab);
    }, [isEditMode, activeTab, childrenArray.length]);

    /**
     * Get the cardType for the current active tab
     * Returns 'none' as default to show all content when cardType is not specified
     */
    const activeTabCardType = useMemo((): CardType => {
      if (!tabDetails || !tabDetails[activeTab]) {
        return 'none';
      }
      return tabDetails[activeTab].cardType || 'none';
    }, [activeTab, tabDetails]);

    /**
     * Dispatch custom event for DST URL updates when tab changes
     * Effect runs when active tab or tab details change
     */
    useEffect(() => {
      if (!Array.isArray(tabDetails)) return;

      const currentTab = tabDetails[activeTab];
      if (!currentTab) return;

      const tabNameLower = currentTab.tabName.toLowerCase();
      // Check if tab is business or personal type
      if (tabNameLower === COBRAND_CARD_TYPE.BUSINESS || tabNameLower === COBRAND_CARD_TYPE.PERSONAL) {
        const customEvent = new CustomEvent('updateCobrandDSTUrl', {
          detail: {
            tabType: tabNameLower,
            cardType: activeTabCardType,
          },
        });
        document.dispatchEvent(customEvent);
      }
    }, [activeTab, tabDetails, activeTabCardType]);

    /**
     * Handle tab click with useCallback for performance
     * Prevents unnecessary re-renders of child components
     */
    const handleTabClick = useCallback((tabIndex: number) => {
      setActiveTab(tabIndex);
    }, []);

    /**
     * Memoized header text component
     * Only re-renders when header content or styling changes
     */
    const headerText = useMemo(() => {
      if (!header) return null;
      return (
        <Heading
          customClass={clsx('mb-2')}
          variation={headingType.title}
          element={headerTag}
          dangerouslySet={header}
          fontSize={headerFontSize as typeof size.small}
        />
      );
    }, [header, headerFontSize, headerTag]);

    /**
     * Memoized subheader text component
     * Adjusts margin based on presence of main header
     */
    const subHeaderText = useMemo(() => {
      if (!subHeader) return null;
      return (
        <Heading
          element={subHeaderTag}
          variation={headingType.subtitle}
          customClass={clsx(!header ? 'mt-3' : '', 'mb-3 mb-md-5')}
          dangerouslySet={subHeader}
          fontSize={subFontSize as size}
        />
      );
    }, [subHeader, subFontSize, subHeaderTag, header]);

    /**
     * Memoized description block component
     * Adjusts padding based on presence of header/subheader
     */
    const descriptionBlock = useMemo(() => {
      if (!descriptionText) return null;
      return (
        <RichText
          text={descriptionText}
          componentId={`${componentId}-TabContainerDescriptionText`}
          customClass={clsx(!subHeader && !header ? 'mt-3' : 'pb-5', descFontSize)}
        />
      );
    }, [descriptionText, descFontSize, componentId, subHeader, header]);

    /**
     * Memoized header section container
     * Contains all header elements with proper alignment
     */
    const TabContainerHeader = useMemo(() => {
      // Return null if no header content exists
      if (!headerText && !subHeaderText && !descriptionBlock) return null;

      return (
        <div
          className={clsx(
            'tab-container-header-section d-flex flex-column',
            contentBlockAlignment === 'center' && 'align-items-center text-center',
            contentBlockAlignment === 'left' && 'align-items-start'
          )}
        >
          {headerText}
          {subHeaderText}
          {descriptionBlock}
        </div>
      );
    }, [headerText, subHeaderText, descriptionBlock, contentBlockAlignment]);

    /**
     * Render tab list items
     * Memoized to prevent re-rendering on every state change
     */
    const renderTabListItems = useMemo(() => {
      if (!tabDetails || tabDetails.length === 0) return null;

      return tabDetails.map((tab, key) => {
        const { tabName = '', tabSubHeader } = tab;
        const isActive = activeTab === key;

        return (
          <li
            key={key}
            role="presentation"
            className={clsx('custom_click_track', 'm-standard-tab-list-item pb-0 text-center mr-0', {
              active: isActive,
            })}
            {...{
              custom_click_track_value: `${tabName}-tab`,
            }}
          >
            <Button
              ariaExpanded={isActive}
              custom_click_track_value={`${tabName}-tab`}
              trackingProperties={trackingProperties}
              callback={() => handleTabClick(key)}
              {...{
                role: 'tab',
                'aria-label': tabName,
                'aria-selected': isActive,
              }}
            >
              <RichText
                text={tabName}
                componentId={`${componentId}-TabContainerTabName-${key}`}
                customClass={clsx(
                  contentBlockAlignment === 'center' && 'text-center',
                  'tabTitleText mb-2',
                  componentPath ? descFontSize : 't-font-m'
                )}
              />
              {tabSubHeader && (
                <RichText
                  text={tabSubHeader}
                  componentId={`${componentId}-TabContainertabSubHeader-${key}`}
                  customClass={clsx(
                    contentBlockAlignment === 'center' && 'text-center',
                    'tab-subtitle',
                    componentPath ? descFontSize : 't-font-m'
                  )}
                />
              )}
            </Button>
          </li>
        );
      });
    }, [
      tabDetails,
      activeTab,
      contentBlockAlignment,
      componentPath,
      descFontSize,
      componentId,
      trackingProperties,
      handleTabClick,
    ]);

    return (
      <StyledTab
        data-component-name="m-cobrand-CobrandTabs"
        data-testid="cobrand-CobrandTabs"
        className={clsx(styleclass, customClass)}
      >
        <div className="container px-0" ref={containerRef}>
          {TabContainerHeader}
          <div id={componentId} className={clsx('col-12 px-0')}>
            <div
              className={clsx(
                'd-flex flex-column mb-2 mb-md-0 px-2 mx-md-1',
                contentBlockAlignment === 'center' && 'align-items-center',
                contentBlockAlignment === 'left' && 'align-items-start'
              )}
            >
              <ul
                role="tablist"
                className={clsx(
                  'm-standard-tab-list',
                  contentBlockAlignment === 'center' && 'justify-content-lg-center',
                  contentBlockAlignment === 'left' && 'justify-content-start'
                )}
              >
                {renderTabListItems}
              </ul>
            </div>
          </div>
          <div
            className={clsx(
              'tabpage-container',
              borderEnabled && 'tabpage-border',
              contentBlockAlignment === 'center' && 'pt-md-5 pt-2'
            )}
          >
            {childrenArray.map((child, index) => {
              const existingCardType =
                React.isValidElement(child) && typeof child.props === 'object' && child.props !== null
                  ? (child.props as Record<string, unknown>)['cardType']
                  : undefined;

              if (React.isValidElement(child) && typeof child.type !== 'string') {
                return (
                  <div key={`tab-content-${index}`} className={activeStates[index] ? 'd-block' : 'd-none'}>
                    {React.cloneElement(child, {
                      ...(child.props as object),
                      // Pass cardType to child for filtering (backward compatible)
                      cardType: (existingCardType as CardType | undefined) || 'none',
                      isActive: activeStates[index],
                      tabIndex: index,
                    } as Partial<typeof child.props>)}
                  </div>
                );
              }

              return (
                <div key={`tab-content-${index}`} className={activeStates[index] ? 'd-block' : 'd-none'}>
                  {child}
                </div>
              );
            })}
          </div>
        </div>
      </StyledTab>
    );
  }
);

CobrandTabs.displayName = 'CobrandTabs';
